// AddEdit.tsx

import {
  FormDatePicker,
  FormFileInput,
  FormInput,
  FormSelect,
  FormTextarea
} from "@/components";
import {
  Button,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui";
import { capitalize, cn, taskStatus } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { useGetPredefinedTasksQuery, useGetSchoolsQuery } from "@/services";
import useEmployeesQuery from "@/services/employeeManagement/useEmployeeManagementMutation";
import { Trash2, X } from "lucide-react";
import { useState } from "react";
import { FormProvider } from "react-hook-form";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  ASSIGN_TO,
  ASSIGNEES,
  END_DATE,
  NAME,
  NOTES,
  PRIORITY,
  PRIORITY_TYPES,
  ROLE,
  roles,
  SCHOOL,
  START_DATE,
  TASK_ATTACHMENT,
  TASK_ATTACHMENT_NAME,
  TASK_STATUS,
  TASKS,
} from "../consts";
import useAddEdit from "./useAddEdit";
import { NotesHistory } from "../NotesHistory";

const AddEdit = () => {
  const {
    form,
    onSubmit,
    requiredFields,
    isView,
    onCancel,
    isMobile,
    isEdit,
    attachmentRef,
    onAddMoreClick,
    onRemoveTask,
    taskType,
    setTaskType,
    selectedPredefinedTasks,
    onSelectPredefinedTask,
    onRemovePredefinedTask,
    taskNotes
  } = useAddEdit();

  const { errors } = form.formState;
  const { action, id: schoolId } = useParams();

  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(
    undefined
  );

  // Fetch categories
  const extractSchoolIdFromPath = () => {
    const pathSegments = location.pathname.split("/");

    // URL structure: /crm-head/school/{schoolId}/{action}/school-task-assignment/...
    const schoolIndex =
      pathSegments.findIndex((segment) => segment === "school") + 1;

    if (schoolIndex > 0 && schoolIndex < pathSegments.length) {
      const potentialSchoolId = pathSegments[schoolIndex];
      // Only return if it's not a reserved action word
      if (!["add", "edit", "view"].includes(potentialSchoolId)) {
        return potentialSchoolId;
      }
    }

    return "";
  };
  // Fetch predefined tasks based on selected category
  const { data: predefinedTasksData, isLoading: isPredefinedLoading } =
    useGetPredefinedTasksQuery(
      { page: pageNumber + 1, limit: pageSize, taskCategory: selectedCategory },
      { enabled: taskType === "predefined" && !!selectedCategory }
    );

  const predefinedTasks = predefinedTasksData?.data?.[0]?.records || [];

  const priorityOptions = Object.values(PRIORITY_TYPES).map((p) => ({
    label: p.charAt(0).toUpperCase() + p.slice(1).toLowerCase(),
    value: p,
  }));

  const statusOptions = Object.values(taskStatus).map((s) => ({
    label: s.label,
    value: s.value,
  }));

  const { data: schoolRes, isLoading: isLoadingSchool } = useGetSchoolsQuery(
    {}
  );
  const schoolOptions =
    schoolRes?.data?.[0]?.records?.map((item: any) => ({
      label: item.schoolName,
      value: item.schoolId,
    })) || [];

  const roleOptions = Object.values(roles).map((r) => ({
    label: r.label,
    value: r.value,
  }));

  const allRoles =
    form
      .watch(TASKS)
      ?.flatMap((task: any) => task?.[ASSIGNEES]?.map((a: any) => a?.[ROLE]))
      ?.filter(Boolean) || [];

  const uniqueRoles = [...new Set(allRoles)];

  const { data: emp_res, isLoading: isLoadingTerms } = useEmployeesQuery({});

  const assignTooptions =
    emp_res?.data?.[0]?.records?.map((item: any) => ({
      label: item.name,
      value: item.profile_id,
    })) || [];

  const loggedInUserData = useSelector(
    (state: RootState) => state.currentData.loggedInUserData
  );

  // 👇 backend sends role as array, e.g. ["CRM_HEAD"]
  const roles2 = loggedInUserData?.role || [];

  // Helper to check if user has a given role
  const hasRole = (role: string) =>
    Array.isArray(roles2) && roles2.includes(role);

  // Extract schoolId from the URL path (helper function)
  const extractedSchoolId = extractSchoolIdFromPath();

  // Use the extracted ID instead of useParams() one
  const disbledfield =
    extractedSchoolId &&
    (hasRole("SALES_OPS") || hasRole("FINANCE_TEAM") || hasRole("CRM_HEAD") || hasRole("SUPER_ADMIN"));


  return (
    <Drawer open={true} direction={isMobile ? "bottom" : "right"}>
      <DrawerContent className="px-6 lg:min-w-[500px] xl:min-w-[730px]">
        <DrawerHeader className="px-0">
          <div className="flex items-center justify-between">
            <DrawerTitle className="text-[20px]">
              {capitalize(action || "")} My Tasks
            </DrawerTitle>
            <X
              onClick={onCancel}
              className="cursor-pointer stroke-g-300 hover:stroke-g-400"
            />
          </div>
        </DrawerHeader>

        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="relative">
            <div className="bg-white rounded-lg py-[25px] max-h-[70vh] lg:max-h-[95vh] overflow-y-auto pb-[100px] px-1">
              {/* Radio buttons for task type */}
              {/* Show radio buttons only when adding a new task */}
              {!isEdit && (
                <div className="flex items-center gap-6 mb-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      value="new"
                      checked={taskType === "new"}
                      onChange={() => setTaskType("new")}
                      className="hidden peer"
                    />
                    <div
                      className={`w-4 h-4 rounded-full border-2 flex items-center justify-center
          ${
            taskType === "new" ? "border-primary bg-primary" : "border-gray-300"
          }
        `}
                    >
                      {taskType === "new" && (
                        <div className="w-2 h-2 rounded-full bg-white" />
                      )}
                    </div>
                    <span
                      className={`${
                        taskType === "new"
                          ? "text-primary font-medium"
                          : "text-gray-700"
                      }`}
                    >
                      New
                    </span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      value="predefined"
                      checked={taskType === "predefined"}
                      onChange={() => setTaskType("predefined")}
                      className="hidden peer"
                    />
                    <div
                      className={`w-4 h-4 rounded-full border-2 flex items-center justify-center
          ${
            taskType === "predefined"
              ? "border-primary bg-primary"
              : "border-gray-300"
          }
        `}
                    >
                      {taskType === "predefined" && (
                        <div className="w-2 h-2 rounded-full bg-white" />
                      )}
                    </div>
                    <span
                      className={`${
                        taskType === "predefined"
                          ? "text-primary font-medium"
                          : "text-gray-700"
                      }`}
                    >
                      Pre-defined task
                    </span>
                  </label>
                </div>
              )}

              <div className="grid grid-cols-1 gap-[25px]">
                {taskType === "new" ? (
                  form.watch(TASKS).map((_task: any, taskIndex: number) => (
                    <div
                      key={`tasks-${taskIndex}`}
                      className={cn(
                        "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[25px] border-b-1 py-5 border-primary"
                      )}
                    >
                      {/* --- New Task Fields --- */}
                      <FormInput
                        name={`${TASKS}.${taskIndex}.${NAME}`}
                        control={form.control}
                        label="Task Name"
                        placeholder="Please enter"
                        error={
                          (errors[TASKS] as any)?.[`${taskIndex}`]?.[NAME]
                            ?.message
                        }
                        isRequired={true}
                        disabled={isView || isEdit}
                      />

                      <FormSelect
                        name={`${TASKS}.${taskIndex}.${PRIORITY}`}
                        control={form.control}
                        label="Priority"
                        placeholder="Please select"
                        error={
                          (errors[TASKS] as any)?.[`${taskIndex}`]?.[PRIORITY]
                            ?.message
                        }
                        isRequired={true}
                        disabled={isView || isEdit}
                        options={priorityOptions}
                      />

                      <FormDatePicker
                        name={`${TASKS}.${taskIndex}.${START_DATE}`}
                        control={form.control}
                        label="Start Date"
                        placeholder="Please select"
                        disabled={isView || isEdit}
                        isRequired={true}
                      />
                      <FormDatePicker
                        name={`${TASKS}.${taskIndex}.${END_DATE}`}
                        control={form.control}
                        label="End Date"
                        placeholder="Please select"
                        disabled={isView || isEdit}
                        isRequired={true}
                      />

                      <FormSelect
                        name={`${TASKS}.${taskIndex}.${TASK_STATUS}`}
                        control={form.control}
                        label="Task Status"
                        placeholder="Please select"
                        disabled={isView || disbledfield}
                        isRequired={true}
                        options={statusOptions}
                      />

                      <FormFileInput
                        fileInputRef={attachmentRef}
                        name={`${TASKS}.${taskIndex}.${TASK_ATTACHMENT}`}
                        control={form.control}
                        label="Task Attachment"
                        maxSize={5}
                        disabled={isView || disbledfield}
                        // isRequired={true}
                        fileName={form.watch(
                          `${TASKS}.${taskIndex}.${TASK_ATTACHMENT_NAME}`
                        )} // ✅ This will display the existing file name
                      />

                      <div className="col-span-1 md:col-span-2 lg:col-span-3">
                        <FormTextarea
                          name={`${TASKS}.${taskIndex}.${NOTES}`}
                          control={form.control}
                          label="Task Notes"
                          placeholder="Please enter"
                          disabled={isView || disbledfield}
                          isRequired={true}
                           error={
                          (errors[TASKS] as any)?.[`${taskIndex}`]?.[NOTES]
                            ?.message
                        }
                        />
                      </div>

                      {/* Assignments Section */}
                      <div className="col-span-1 md:col-span-2 lg:col-span-3 bg-[#FFF5E9] gap-[25px] p-4">
                        <div className="form-select-wrapper">
                          {form
                            .watch(`${TASKS}.0.${ASSIGNEES}`)
                            ?.map((_assign: any, assignIndex: number) => {
                              const selectedRole = form.watch(
                                `${TASKS}.0.${ASSIGNEES}.${assignIndex}.${ROLE}`
                              );
                              return (
                                <div
                                  key={`assign-0-${assignIndex}`}
                                  className="grid grid-cols-1 md:grid-cols-3 gap-[20px] bg-[#FFF5E9] p-3 rounded-md items-end"
                                >
                                  {/* School Field */}
                                  <FormSelect
                                    name={`${TASKS}.0.${ASSIGNEES}.${assignIndex}.${SCHOOL}`}
                                    control={form.control}
                                    label="School"
                                    placeholder="Please select"
                                    error={
                                      (errors[TASKS] as any)?.[0]?.[
                                        ASSIGNEES
                                      ]?.[assignIndex]?.[SCHOOL]?.message
                                    }
                                    isRequired={true}
                                    disabled={isView || isEdit}
                                    options={schoolOptions}
                                  />

                                  {/* Role Field */}
                                  <FormSelect
                                    name={`${TASKS}.0.${ASSIGNEES}.${assignIndex}.${ROLE}`}
                                    control={form.control}
                                    label="Role"
                                    placeholder="Please select"
                                    error={
                                      (errors[TASKS] as any)?.[0]?.[
                                        ASSIGNEES
                                      ]?.[assignIndex]?.[ROLE]?.message
                                    }
                                    isRequired={true}
                                    disabled={isView || isEdit}
                                    options={roleOptions}
                                  />

                                  {/* Assign To Field */}
                                  <div className="flex items-end gap-3">
                                    <div className="w-full">
                                      <FormSelect
                                        name={`${TASKS}.0.${ASSIGNEES}.${assignIndex}.${ASSIGN_TO}`}
                                        control={form.control}
                                        label="Assign To"
                                        placeholder="Please select"
                                        disabled={isView || isEdit}
                                        options={assignTooptions}
                                      />
                                    </div>
                                    {assignIndex > 0 && (
                                      <Trash2
                                        size={20}
                                        className="stroke-red-500 cursor-pointer hover:scale-110 transition-transform"
                                        onClick={() =>
                                          onRemoveTask(0, assignIndex)
                                        }
                                      />
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                        {/* Add More Button */}
                        {!isEdit && (
                          <div className="mt-4 flex justify-end">
                            <button
                              type="button"
                              onClick={() => onAddMoreClick(0)}
                              className="text-primary hover:underline"
                            >
                              + Add More
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  // --- Predefined Task Shared Section ---
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[25px] border-b-1 py-5 border-primary">
                    {/* Category */}
                    {/* <FormSelect
        name="selectedCategory"
        control={form.control}
        label="Category"
        placeholder="Please select"
        options={categories}
        isRequired={true}
        disabled={isView || isLoadingCategories}
        onChange={(val: string | undefined) => setSelectedCategory(val)}
        error={errors.selectedCategory?.message}
      /> */}

                    {/* Start / End Date */}
                    <FormDatePicker
                      name={`${TASKS}.0.${START_DATE}`}
                      control={form.control}
                      label="Start Date"
                      placeholder="Please select"
                      disabled={isView || isEdit}
                      isRequired={true}
                    />
                    <FormDatePicker
                      name={`${TASKS}.0.${END_DATE}`}
                      control={form.control}
                      label="End Date"
                      placeholder="Please select"
                      disabled={isView || isEdit}
                      isRequired={true}
                    />

                    {/* Task Status */}
                    <FormSelect
                      name={`${TASKS}.0.${TASK_STATUS}`}
                      control={form.control}
                      label="Task Status"
                      placeholder="Please select"
                      disabled={isView}
                      isRequired={true}
                      options={statusOptions}
                    />

                    {/* Attachment */}
                    <FormFileInput
                      fileInputRef={attachmentRef}
                      name={`${TASKS}.0.${TASK_ATTACHMENT}`}
                      control={form.control}
                      label="Task Attachment"
                      maxSize={5}
                      disabled={isView}
                      // isRequired={true}
                    />

                    {/* Predefined Tasks Table */}
                    {selectedCategory && (
                      <div className="col-span-3">
                        <div className="overflow-x-auto border border-gray-200 rounded-lg mt-3">
                          <table className="min-w-full text-sm text-left text-gray-700">
                            <thead className="bg-[#FFF5E9]">
                              <tr>
                                <th className="px-4 py-3 font-semibold">
                                  Task Name
                                </th>
                                <th className="px-4 py-3 font-semibold">
                                  Priority
                                </th>
                                <th className="px-4 py-3 font-semibold">
                                  Notes
                                </th>
                                <th className="px-4 py-3 font-semibold text-center">
                                  Action
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {isPredefinedLoading ? (
                                <tr>
                                  <td
                                    colSpan={4}
                                    className="px-4 py-3 text-center text-gray-400 italic"
                                  >
                                    Loading predefined tasks...
                                  </td>
                                </tr>
                              ) : predefinedTasks.length > 0 ? (
                                predefinedTasks.map(
                                  (task: any, index: number) => {
                                    const isSelected =
                                      selectedPredefinedTasks.some(
                                        (t: any) => t._id === task._id
                                      );
                                    return (
                                      <tr
                                        key={task._id || index}
                                        className={cn(
                                          "border-t hover:bg-gray-50 cursor-pointer",
                                          isSelected ? "bg-blue-100" : ""
                                        )}
                                        onClick={() =>
                                          onSelectPredefinedTask(task)
                                        }
                                      >
                                        <td className="px-4 py-3 text-primary">
                                          {task.taskName}
                                        </td>
                                        <td className="px-4 py-3">
                                          {task.priority}
                                        </td>
                                        <td className="px-4 py-3">
                                          {task.taskDescription || "-"}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                          {isSelected && (
                                            <Trash2
                                              size={18}
                                              className="stroke-red-500 cursor-pointer hover:scale-110 transition-transform inline"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                onRemovePredefinedTask(task);
                                              }}
                                            />
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  }
                                )
                              ) : (
                                <tr>
                                  <td
                                    colSpan={4}
                                    className="px-4 py-3 text-center text-gray-400 italic"
                                  >
                                    No predefined tasks found.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>

                        {selectedPredefinedTasks.length > 0 && (
                          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-gray-700 mt-3">
                            <strong>Selected Tasks:</strong>
                            <ul className="list-disc ml-5">
                              {selectedPredefinedTasks.map((t) => (
                                <li key={t._id}>
                                  {t.taskName} ({t.priority})
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    {/* --- Assignments Section (your full version) --- */}
                    <div className="col-span-1 md:col-span-2 lg:col-span-3 bg-[#FFF5E9] gap-[25px] p-4">
                      <div className="form-select-wrapper">
                        {form
                          .watch(`${TASKS}.0.${ASSIGNEES}`)
                          ?.map((_assign: any, assignIndex: number) => {
                            const selectedRole = form.watch(
                              `${TASKS}.0.${ASSIGNEES}.${assignIndex}.${ROLE}`
                            );
                            return (
                              <div
                                key={`assign-0-${assignIndex}`}
                                className="grid grid-cols-1 md:grid-cols-3 gap-[20px] bg-[#FFF5E9] p-3 rounded-md items-end"
                              >
                                {/* School Field */}
                                <FormSelect
                                  name={`${TASKS}.0.${ASSIGNEES}.${assignIndex}.${SCHOOL}`}
                                  control={form.control}
                                  label="School"
                                  placeholder="Please select"
                                  error={
                                    (errors[TASKS] as any)?.[0]?.[ASSIGNEES]?.[
                                      assignIndex
                                    ]?.[SCHOOL]?.message
                                  }
                                  isRequired={true}
                                  disabled={isView || isEdit}
                                  options={schoolOptions}
                                />

                                {/* Role Field */}
                                <FormSelect
                                  name={`${TASKS}.0.${ASSIGNEES}.${assignIndex}.${ROLE}`}
                                  control={form.control}
                                  label="Role"
                                  placeholder="Please select"
                                  error={
                                    (errors[TASKS] as any)?.[0]?.[ASSIGNEES]?.[
                                      assignIndex
                                    ]?.[ROLE]?.message
                                  }
                                  isRequired={true}
                                  disabled={isView || isEdit}
                                  options={roleOptions}
                                />

                                {/* Assign To Field */}
                                {/* <div className="flex items-end gap-3">
                  <div className="w-full">
                    <FormSelect
                        name={`${TASKS}.0.${ASSIGNEES}.${assignIndex}.${ASSIGN_TO}`}
                        control={form.control}
                        label="Assign To"
                        placeholder="Please select"
                        error={
                          (errors[TASKS] as any)?.[0]?.[ASSIGNEES]?.[assignIndex]?.[ASSIGN_TO]?.message
                        }
                        isRequired={true}
                        disabled={!selectedRole || isView || isEdit}
                        options={employeesByRole[selectedRole] || []}
                      />
                  </div>
                  {assignIndex > 0 && (
                    <Trash2
                      size={20}
                      className="stroke-red-500 cursor-pointer hover:scale-110 transition-transform"
                      onClick={() => onRemoveTask(0, assignIndex)}
                    />
                  )}
                </div> */}
                              </div>
                            );
                          })}
                      </div>
                      {/* Add More Button */}
                      {!isEdit && (
                        <div className="mt-4 flex justify-end">
                          <button
                            type="button"
                            onClick={() => onAddMoreClick(0)}
                            className="text-primary hover:underline"
                          >
                            + Add More
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-6">
                <NotesHistory
                  notes={taskNotes}
                />
              </div>
            </div>

            <div className="flex flex-row justify-end gap-3 py-3 absolute bottom-5 w-full bg-white pb-[25px] z-[3]">
              <Button variant="outline" type="button" onClick={onCancel}>
                Cancel
              </Button>
              {isView ? null : (
                <Button type="submit">{isEdit ? "Update" : "Submit"}</Button>
              )}
            </div>
          </form>
        </FormProvider>
      </DrawerContent>
    </Drawer>
  );
};

export default AddEdit;
